import React, { useEffect, useState, FormEvent } from "react";
import classNames from "classnames";
import { Alert } from "@cmsgov/ds-medicare-gov";
import { useFlags } from "launchdarkly-react-client-sdk";
import { County } from "../../@types";
import { getCounties } from "../../api";
import { useTranslate } from "../../helpers/intlHooks";
import {
  CoverageSelectorFormValues,
  CoverageSelectorInitialValues,
  CoverageSelectorSelectedValues,
  ErrorType,
} from "./types";
import { isValidZipcode } from "../../helpers/zipcodeHelpers";
import { getYearPartInfo } from "../../helpers/yearPartHelpers";
import {
  AnalyticsButton,
  AnalyticsButtonStyle,
  AnalyticsButtonType,
} from "../../app/contexts/Analytics/types";
import { BadgeWithIcon, ContinueWithoutLoggingInIcon } from "../../components";
import {
  PlanTypeStep,
  PlanTypeStepActions,
  ZipcodeAndCoverageYearStep,
  ZipcodeAndCoverageYearStepActions,
} from "./components";

interface CoverageSelectorV2Props {
  initialFormValues?: CoverageSelectorInitialValues;
  onApply: (
    values: CoverageSelectorSelectedValues,
    analyticsButton?: AnalyticsButton
  ) => void;
  isInModal?: boolean;
  handleCancel?: () => void;
}

export interface ErrorState {
  error: ErrorType | null;
  message: string | undefined;
}

enum ActiveStep {
  ZIPCODE,
  PLAN_TYPE,
}

export const CoverageSelectorV2: React.FunctionComponent<
  CoverageSelectorV2Props
> = ({ initialFormValues, onApply, isInModal = false, handleCancel }) => {
  const t = useTranslate();
  const flags = useFlags();
  const { isPublicPreview, isOpenEnrollment, isOpenEnrollmentNextYearOnly } =
    getYearPartInfo(flags);
  const { mctCurrentPlanYear } = flags;

  const [fetching, setFetching] = useState(false);
  const [activeStep, setActiveStep] = useState<ActiveStep>(ActiveStep.ZIPCODE);
  const [errorState, setErrorState] = useState<{
    zipcode: ErrorState;
    planType: ErrorState;
    counties: ErrorState;
  }>({
    zipcode: { error: null, message: undefined },
    planType: { error: null, message: undefined },
    counties: { error: null, message: undefined },
  });
  const [countyResults, setCountyResults] = useState<County[]>([]);
  const showCoverageYearSelector =
    isPublicPreview || isOpenEnrollment || isOpenEnrollmentNextYearOnly;
  const [currentFormValues, setCurrentFormValues] =
    useState<CoverageSelectorFormValues>({
      coverageYear: showCoverageYearSelector
        ? mctCurrentPlanYear + 1
        : mctCurrentPlanYear,
      zipcode: initialFormValues?.zipcode,
      coverageType: initialFormValues?.coverageType,
    });
  const [initialFips, setInitialFips] = useState(initialFormValues?.fips);

  useEffect(() => {
    if (initialFormValues?.zipcode) {
      fetchCounties(initialFormValues.zipcode, initialFormValues.fips);
    }
  }, []);

  const findPlansButtonText = t("coverage_selector.find_plans");

  const analyticsButton = {
    text: findPlansButtonText,
    buttonStyle: AnalyticsButtonStyle.PRIMARY,
    buttonType: AnalyticsButtonType.SUBMIT,
  };

  const isPlanTypeFormInvalid = (): boolean => {
    let hasError = false;

    if (!currentFormValues.coverageType) {
      setErrorState(errorState => ({
        ...errorState,
        planType: {
          error: ErrorType.MISSING,
          message: t("coverage_selector.error.plan_type"),
        },
      }));
      hasError = true;
    } else {
      setErrorState(errorState => ({
        ...errorState,
        planType: {
          error: null,
          message: undefined,
        },
      }));
    }
    return hasError;
  };

  const isZipcodeFormInvalid = (): boolean => {
    let hasError = false;

    if (errorState.zipcode.error === ErrorType.API) {
      // want to keep the api error
      hasError = true;
    } else {
      if (!isValidZipcode(currentFormValues.zipcode)) {
        setErrorState(errorState => ({
          ...errorState,
          zipcode: {
            error:
              currentFormValues.zipcode?.length === 0
                ? ErrorType.MISSING
                : ErrorType.INVALID,
            message: t("coverage_selector.error.zip_code"),
          },
        }));
        hasError = true;
      } else {
        // valid zipcode
        if (!currentFormValues.county) {
          setErrorState(errorState => ({
            ...errorState,
            counties: {
              error: ErrorType.MISSING,
              message: "coverage_selector.error.missing_county",
            },
          }));
          hasError = true;
        } else {
          setErrorState(errorState => ({
            ...errorState,
            counties: {
              error: null,
              message: undefined,
            },
          }));
        }
      }
    }
    return hasError;
  };

  const fetchCounties = async (
    zipcode: string,
    initialFips?: string
  ): Promise<void> => {
    setFetching(true);
    try {
      const fetchedCounties = await getCounties(zipcode);
      setCountyResults(fetchedCounties);

      if (initialFips) {
        const foundCounty = fetchedCounties.find(
          county => county.fips === initialFips
        );
        if (foundCounty) {
          setCurrentFormValues(currentFormValues => ({
            ...currentFormValues,
            county: foundCounty,
          }));
        } else {
          if (fetchedCounties.length === 1) {
            setCurrentFormValues(currentFormValues => ({
              ...currentFormValues,
              county: fetchedCounties[0],
            }));
          }
        }
      } else {
        if (fetchedCounties.length === 1) {
          setCurrentFormValues(currentFormValues => ({
            ...currentFormValues,
            county: fetchedCounties[0],
          }));
        }
      }
    } catch (err) {
      setErrorState(errorState => ({
        ...errorState,
        zipcode: {
          error: ErrorType.API,
          message: t("coverage_selector.error.zip_code_not_found"),
        },
      }));
    }
    setFetching(false);
  };

  const onZipcodeChange = (zipcodeInput: string) => {
    setErrorState(errorState => ({
      ...errorState,
      counties: {
        error: null,
        message: undefined,
      },
      zipcode: {
        error: null,
        message: undefined,
      },
    }));
    setCountyResults([]);
    setInitialFips(undefined);
    setCurrentFormValues(currentFormValues => ({
      ...currentFormValues,
      zipcode: zipcodeInput,
      county: undefined,
    }));

    if (isValidZipcode(zipcodeInput)) {
      fetchCounties(zipcodeInput);
    }
  };

  const onClickContinue = (e?: FormEvent<HTMLFormElement>): void => {
    e?.preventDefault();
    if (!isZipcodeFormInvalid()) {
      setActiveStep(ActiveStep.PLAN_TYPE);
      setErrorState({
        zipcode: { error: null, message: undefined },
        planType: { error: null, message: undefined },
        counties: { error: null, message: undefined },
      });
    }
  };

  const onClickStart = (e: FormEvent<HTMLFormElement>): void => {
    e.preventDefault();
    if (activeStep === ActiveStep.PLAN_TYPE) {
      if (!isPlanTypeFormInvalid()) {
        onApply(
          currentFormValues as CoverageSelectorSelectedValues,
          analyticsButton
        );
      }
    } else {
      onClickContinue();
    }
  };

  return (
    <>
      {activeStep === ActiveStep.ZIPCODE && !isInModal && (
        <BadgeWithIcon
          svgIcon={ContinueWithoutLoggingInIcon}
          theme="primary-alt-lightest"
          className="ds-u-margin-bottom--2"
        >
          {t("landing_page.badge.continue_without_logging_in")}
        </BadgeWithIcon>
      )}
      <div
        className={classNames("mct-c-coverage-selector-v2", {
          "mct-c-coverage-selector-v2--in-modal": isInModal,
        })}
        data-testid="mct-c-coverage-selector-v2"
      >
        <form onSubmit={onClickStart}>
          {(errorState.planType.error ||
            errorState.zipcode.error ||
            errorState.counties.error) && (
            <div data-testid="alert" className="ds-u-margin-bottom--2">
              <Alert
                heading={t("coverage_selector.alert.heading")}
                variation="error"
                className="mct-c-coverage-selector-v2__alert"
              >
                <span>{t("coverage_selector.alert.message")}</span>
              </Alert>
            </div>
          )}
          <div className="mct-c-coverage-selector-v2__step">
            {activeStep === ActiveStep.ZIPCODE ? (
              <ZipcodeAndCoverageYearStep
                {...{
                  countiesErrorState: errorState.counties,
                  countyResults,
                  currentFips: currentFormValues.county?.fips,
                  fetching,
                  initialFips,
                  isOpenEnrollmentNextYearOnly,
                  mctCurrentPlanYear,
                  onCountyChange: county => {
                    setCurrentFormValues(formValues => ({
                      ...formValues,
                      county: JSON.parse(county),
                    }));
                  },
                  onCoverageYearChange: coverageYear => {
                    setCurrentFormValues(formValues => ({
                      ...formValues,
                      coverageYear: +coverageYear,
                    }));
                  },
                  onZipcodeChange,
                  selectedCoverageYear: currentFormValues.coverageYear,
                  selectedZipcode: currentFormValues.zipcode,
                  showCoverageYearSelector,
                  zipcode: currentFormValues.zipcode,
                  zipcodeErrorState: errorState.zipcode,
                }}
              />
            ) : (
              <PlanTypeStep
                {...{
                  isInModal,
                  planTypeErrorState: errorState.planType,
                  onPlanTypeChange: planType => {
                    setCurrentFormValues({
                      ...currentFormValues,
                      coverageType: planType,
                    });
                    setErrorState(errorState => ({
                      ...errorState,
                      planType: {
                        error: null,
                        message: undefined,
                      },
                    }));
                  },
                }}
              />
            )}
          </div>
          <div className="ds-u-padding-top--2">
            {activeStep === ActiveStep.ZIPCODE ? (
              <ZipcodeAndCoverageYearStepActions
                {...{
                  onClickContinue,
                  isInModal,
                  handleCancel,
                }}
              />
            ) : (
              <PlanTypeStepActions
                {...{
                  findPlansButtonText,
                  handleGoBack: () => {
                    setActiveStep(ActiveStep.ZIPCODE);
                    setErrorState({
                      zipcode: { error: null, message: undefined },
                      planType: { error: null, message: undefined },
                      counties: { error: null, message: undefined },
                    });
                    setCurrentFormValues(currentForm => ({
                      ...currentForm,
                      coverageType: undefined,
                    }));
                  },
                }}
              />
            )}
          </div>
        </form>
      </div>
    </>
  );
};
